/** Static taxonomy for the audit log UI; unknown event_types fall through to "other". */

import { BadgeVariant } from "@/components/ui/Badge";

export type AuditCategory =
  | "auth"
  | "provisioning"
  | "deprovisioning"
  | "settings"
  | "security"
  | "ssoTest"
  | "workspace"
  | "other";

export type AuditSeverity = "info" | "warning" | "critical";

interface TaxonomyEntry {
  category: AuditCategory;
  severity: AuditSeverity;
  /** Human-readable label for the filter UI. */
  label: string;
}

export const EVENT_TAXONOMY: Record<string, TaxonomyEntry> = {
  // Auth
  sso_login_success: { category: "auth", severity: "info", label: "SSO login succeeded" },
  sso_login_failed: { category: "auth", severity: "warning", label: "SSO login failed" },
  sso_enforcement_blocked: {
    category: "auth",
    severity: "warning",
    label: "Password login blocked (SSO required)",
  },
  sso_rejected_deactivated_identity: {
    category: "security",
    severity: "critical",
    label: "SSO rejected for deactivated identity",
  },

  // Provisioning
  jit_user_provisioned: {
    category: "provisioning",
    severity: "info",
    label: "JIT user provisioned",
  },
  jit_user_denied: {
    category: "provisioning",
    severity: "warning",
    label: "JIT user denied",
  },
  directory_user_created: {
    category: "provisioning",
    severity: "info",
    label: "Directory user created",
  },
  directory_user_updated: {
    category: "provisioning",
    severity: "info",
    label: "Directory user updated",
  },
  directory_group_created: {
    category: "provisioning",
    severity: "info",
    label: "Directory group created",
  },
  directory_group_updated: {
    category: "provisioning",
    severity: "info",
    label: "Directory group updated",
  },
  directory_group_deleted: {
    category: "deprovisioning",
    severity: "warning",
    label: "Directory group deleted",
  },
  directory_group_user_added: {
    category: "provisioning",
    severity: "info",
    label: "User added to directory group",
  },
  directory_group_user_removed: {
    category: "deprovisioning",
    severity: "warning",
    label: "User removed from directory group",
  },
  group_role_mapping_created: {
    category: "settings",
    severity: "info",
    label: "Group → role mapping created",
  },
  group_role_mapping_updated: {
    category: "settings",
    severity: "info",
    label: "Group → role mapping updated",
  },
  group_role_mapping_deleted: {
    category: "settings",
    severity: "warning",
    label: "Group → role mapping deleted",
  },
  group_mapping_blocked_last_admin: {
    category: "security",
    severity: "critical",
    label: "Group mapping blocked (would orphan last Admin)",
  },

  // Deprovisioning
  directory_user_deactivated: {
    category: "deprovisioning",
    severity: "warning",
    label: "Directory user deactivated",
  },
  reactivation_attempt_rejected: {
    category: "security",
    severity: "critical",
    label: "Reactivation attempt rejected",
  },

  // Workspace
  workspace_membership_granted: {
    category: "workspace",
    severity: "info",
    label: "Workspace membership granted",
  },
  workspace_membership_revoked: {
    category: "workspace",
    severity: "warning",
    label: "Workspace membership revoked",
  },
  workspace_role_changed: {
    category: "workspace",
    severity: "info",
    label: "Workspace role changed",
  },
  workspace_role_downgrade_blocked: {
    category: "security",
    severity: "warning",
    label: "Workspace role downgrade blocked",
  },
  ownership_transferred: {
    category: "workspace",
    severity: "info",
    label: "Ownership transferred",
  },
  workspace_orphaned: {
    category: "security",
    severity: "critical",
    label: "Workspace orphaned",
  },

  // Settings / policy
  identity_settings_updated: {
    category: "settings",
    severity: "info",
    label: "Identity settings updated",
  },
  identity_policy_version_bumped: {
    category: "settings",
    severity: "info",
    label: "Policy version bumped (sessions invalidated)",
  },
  enterprise_identity_enabled: {
    category: "settings",
    severity: "info",
    label: "Enterprise identity enabled",
  },
  enterprise_identity_disabled: {
    category: "settings",
    severity: "warning",
    label: "Enterprise identity disabled",
  },
  require_sso_enabled: {
    category: "settings",
    severity: "info",
    label: "Require SSO enabled",
  },
  require_sso_disabled: {
    category: "settings",
    severity: "warning",
    label: "Require SSO disabled",
  },
  sso_setup_started: { category: "settings", severity: "info", label: "SSO setup started" },
  sso_setup_saved: { category: "settings", severity: "info", label: "SSO setup saved" },
  sso_connection_activated: {
    category: "settings",
    severity: "info",
    label: "SSO connection activated",
  },
  directory_sync_activated: {
    category: "settings",
    severity: "info",
    label: "Directory Sync activated",
  },

  // Security
  break_glass_guardrail_blocked: {
    category: "security",
    severity: "warning",
    label: "Require SSO blocked (Owner MFA required)",
  },
  break_glass_login_used: {
    category: "security",
    severity: "warning",
    label: "Emergency access login used",
  },
  break_glass_login_blocked: {
    category: "security",
    severity: "info",
    label: "Emergency access login blocked (SSO healthy)",
  },
  mfa_disabled_for_managed_user: {
    category: "security",
    severity: "warning",
    label: "MFA disabled for managed user",
  },
  scim_role_grant_blocked: {
    category: "security",
    severity: "warning",
    label: "SCIM role grant blocked",
  },

  // SSO test
  sso_test_started: { category: "ssoTest", severity: "info", label: "SSO test started" },
  sso_test_succeeded: { category: "ssoTest", severity: "info", label: "SSO test succeeded" },
  sso_test_failed: { category: "ssoTest", severity: "warning", label: "SSO test failed" },
};

const FALLBACK: TaxonomyEntry = {
  category: "other",
  severity: "info",
  label: "",
};

export const getTaxonomy = (eventType: string): TaxonomyEntry =>
  EVENT_TAXONOMY[eventType] ?? { ...FALLBACK, label: eventType };

export const SEVERITY_DOT_CLASSES: Record<AuditSeverity, string> = {
  info: "bg-blue-500",
  warning: "bg-amber-500",
  critical: "bg-red-500",
};

/** Badge color per category; kept separate from severity so a critical
 *  auth event still reads as auth rather than a generic red badge. */
export const CATEGORY_BADGE_VARIANT: Record<AuditCategory, BadgeVariant> = {
  auth: BadgeVariant.BLUE,
  provisioning: BadgeVariant.GREEN,
  deprovisioning: BadgeVariant.ORANGE,
  settings: BadgeVariant.GRAY,
  security: BadgeVariant.RED,
  ssoTest: BadgeVariant.PURPLE,
  workspace: BadgeVariant.AMBER,
  other: BadgeVariant.GRAY,
};

export const CATEGORY_LABEL: Record<AuditCategory, string> = {
  auth: "Auth",
  provisioning: "Provisioning",
  deprovisioning: "Deprovisioning",
  settings: "Settings",
  security: "Security",
  ssoTest: "SSO test",
  workspace: "Workspace",
  other: "Other",
};

/** Event types sorted by category then alphabetically (filter UI). */
export const getEventTypesGroupedByCategory = (): {
  category: AuditCategory;
  label: string;
  eventTypes: { value: string; label: string }[];
}[] => {
  const grouped: Record<AuditCategory, { value: string; label: string }[]> = {
    auth: [],
    provisioning: [],
    deprovisioning: [],
    settings: [],
    security: [],
    ssoTest: [],
    workspace: [],
    other: [],
  };
  Object.entries(EVENT_TAXONOMY).forEach(([value, { category, label }]) => {
    grouped[category].push({ value, label });
  });
  return Object.entries(grouped)
    .filter(([, items]) => items.length > 0)

    .map(([category, items]) => ({
      category: category as AuditCategory,
      label: CATEGORY_LABEL[category as AuditCategory],
      eventTypes: items.sort((a, b) => a.label.localeCompare(b.label)),
    }));
};
